import React from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import BusinessCategoryField from 'src/js/modules/ai/components/fields/business-category-field';
import TextAreaWithLengthAnnotationField from 'src/js/modules/common/components/text-area-with-length-annotation-field';
import regexps from '../constants/regexps';
import settingTypes from '../constants/setting-types';
import settingLengthLimits from '../constants/setting-length-limits';
import { useWebsiteSettingsContext } from '../context/website-settings-context';

const { HeadingGroup, ControlGroup, Checkbox, Box, Stack, AnimatedBanner, InputGroupField } =
  designSystem;

const { MIN_BUSINESS_DESCRIPTION_CHARS, MAX_BUSINESS_DESCRIPTION_CHARS } = settingLengthLimits;

// Normalize the string to remove invisible characters
const normalizeValue = (value) =>
  value?.normalize().replace(/[\u200B-\u200D\uFEFF\u202C\u202D]/g, '');

const BusinessInfoSection = ({ addressesRef, phonesRef, emailsRef, businessHoursRef }) => {
  const {
    control,
    captions: { businessInfo: captions },
    formValues: formData,
    isBusinessNameSameAsSiteName,
    setIsBusinessNameSameAsSiteName,
    setFieldValue,
    areAiFeaturesAvailable,
    aiTutorialUrl,
    onAiBannerClick,
    onBusinessCategoryChange,
    onBusinessCategoryFocus,
  } = useWebsiteSettingsContext();
  const siteName = formData[settingTypes.SITE_NAME];

  const handleBusinessNameChange = (value) => {
    const trimmedValue = value.trim();

    if (trimmedValue !== siteName) {
      setIsBusinessNameSameAsSiteName(false);
    }
  };

  const handleBusinessNameBlur = (value) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      setFieldValue(settingTypes.BUSINESS_NAME, siteName);
      setIsBusinessNameSameAsSiteName(true);
    }
  };

  const handleBusinessNameAsSiteNameChange = (value) => {
    if (value) {
      setFieldValue(settingTypes.BUSINESS_NAME, siteName);
    }

    setIsBusinessNameSameAsSiteName(value);
  };

  return (
    <Stack gap="spacing-m">
      {areAiFeaturesAvailable && (
        <AnimatedBanner
          appearance="accent"
          title={captions.aiBanner.title}
          description={captions.aiBanner.description}
          actionButtonLabel={captions.aiBanner.buttonLabel}
          isBetaLabelShown
          onActionButtonClick={onAiBannerClick}
          actionButtonHref={aiTutorialUrl}
        />
      )}
      <HeadingGroup title={captions.profileSection.title} />
      <ControlGroup title={captions.businessName.name}>
        <InputGroupField
          control={control}
          name={settingTypes.BUSINESS_NAME}
          placeholder={siteName}
          size="medium"
          onChange={handleBusinessNameChange}
          onBlur={handleBusinessNameBlur}
        />
        <Box marginTop="spacing-2-xs">
          <Checkbox
            id={settingTypes.BUSINESS_NAME}
            label={captions.businessName.checkboxLabel}
            checked={isBusinessNameSameAsSiteName}
            onChange={handleBusinessNameAsSiteNameChange}
            value={settingTypes.BUSINESS_NAME}
          />
        </Box>
      </ControlGroup>
      <ControlGroup title={captions.businessCategory.title}>
        <BusinessCategoryField
          control={control}
          name={settingTypes.BUSINESS_CATEGORY}
          placeholder={captions.businessCategory.placeholder}
          noResultsText={captions.businessCategory.noResultsText}
          onChange={onBusinessCategoryChange}
          onFocus={onBusinessCategoryFocus}
        />
      </ControlGroup>
      <ControlGroup title={captions.businessDescription.name}>
        <div className="ws-text-area-with-length-annotation-field">
          <TextAreaWithLengthAnnotationField
            control={control}
            name={settingTypes.BUSINESS_DESCRIPTION}
            rules={{
              minLength: {
                value: MIN_BUSINESS_DESCRIPTION_CHARS,
                message: captions.errorMessages.businessDescription.minLength,
              },
              maxLength: {
                value: MAX_BUSINESS_DESCRIPTION_CHARS,
                message: captions.errorMessages.businessDescription.maxLength,
              },
            }}
            placeholder={captions.businessDescription.placeholder}
            maxLength={MAX_BUSINESS_DESCRIPTION_CHARS}
            statusText={captions.businessDescription.description}
          />
        </div>
      </ControlGroup>

      <HeadingGroup
        title={captions.contactInfoSection.title}
        description={captions.contactInfoSection.description}
      />
      <div ref={addressesRef}>
        <ControlGroup title={captions.address.title}>
          <InputGroupField
            control={control}
            name={settingTypes.ADDRESSES}
            placeholder={captions.address.placeholder}
            iconGlyph="map-marker"
            size="medium"
          />
        </ControlGroup>
      </div>
      <div ref={phonesRef}>
        <ControlGroup title={captions.phone.title}>
          <InputGroupField
            control={control}
            name={settingTypes.PHONES}
            placeholder={captions.phone.placeholder}
            iconGlyph="phone"
            size="medium"
            processValue={normalizeValue}
            rules={{
              pattern: {
                value: regexps.PHONE,
                message: captions.errorMessages.phone,
              },
            }}
          />
        </ControlGroup>
      </div>
      <div ref={emailsRef}>
        <ControlGroup title={captions.email.title}>
          <InputGroupField
            control={control}
            name={settingTypes.EMAILS}
            placeholder={captions.email.placeholder}
            iconGlyph="mail"
            size="medium"
            rules={{
              pattern: {
                value: regexps.EMAIL,
                message: captions.errorMessages.email,
              },
            }}
          />
        </ControlGroup>
      </div>
      <div ref={businessHoursRef}>
        <ControlGroup title={captions.businessHours.title}>
          <InputGroupField
            control={control}
            name={settingTypes.BUSINESS_HOURS}
            placeholder={captions.businessHours.placeholder}
            iconGlyph="clock"
            size="medium"
          />
        </ControlGroup>
      </div>
    </Stack>
  );
};

BusinessInfoSection.propTypes = {
  phonesRef: PropTypes.shape().isRequired,
  addressesRef: PropTypes.shape().isRequired,
  emailsRef: PropTypes.shape().isRequired,
  businessHoursRef: PropTypes.shape().isRequired,
};

export default BusinessInfoSection;
